import React from 'react';
import { Link } from 'react-router-dom';
import type { Family } from '../../api/families';

interface FamiliesPanelProps {
  families: Family[];
  /** Total documents across the family's members. Keyed by family.id. */
  docCountByFamily: Record<number, number>;
  /** Total pending documents across the family's members. */
  pendingByFamily: Record<number, number>;
  /** Family id whose members include a member shared "owned" by a
   *  different user (i.e. the current user is not the owner). Drives
   *  a small "Partajat" chip on the card. */
  sharedFamilyIds: Set<number>;
}

const IconUsers = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconFile = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const IconArrow = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const FamiliesPanel: React.FC<FamiliesPanelProps> = ({
  families,
  docCountByFamily,
  pendingByFamily,
  sharedFamilyIds,
}) => {
  return (
    <section className="dash-panel dash-panel--families" aria-labelledby="dash-families-title">
      <div className="dash-panel-head">
        <h2 className="dash-panel-title" id="dash-families-title">
          Familiile mele
        </h2>
        <Link to="/families" className="dash-panel-link">
          Toate <IconArrow />
        </Link>
      </div>
      {families.length === 0 ? (
        <div className="dash-panel-empty">
          <p>Nicio familie creată încă.</p>
          <Link to="/families?create=1" className="btn btn-primary btn-sm">
            Adaugă prima familie
          </Link>
        </div>
      ) : (
        <ul className="dash-panel-list">
          {families.map((f) => {
            const docs = docCountByFamily[f.id] ?? 0;
            const pending = pendingByFamily[f.id] ?? 0;
            const isShared = sharedFamilyIds.has(f.id);
            return (
              <li key={f.id}>
                <Link to={`/families/${f.id}`} className="dash-fam-row">
                  <div className="dash-fam-row-main">
                    <div className="dash-fam-row-title">
                      <span className="dash-fam-row-name">{f.name}</span>
                      {isShared && (
                        <span className="dash-fam-row-tag" title="Această familie ți-a fost partajată">
                          Partajat
                        </span>
                      )}
                    </div>
                    <div className="dash-fam-row-meta">
                      <span className="dash-fam-row-meta-item">
                        <IconUsers />
                        {f.members.length}{' '}
                        {f.members.length === 1 ? 'membru' : 'membri'}
                      </span>
                      <span className="dash-fam-row-meta-item">
                        <IconFile />
                        {docs} {docs === 1 ? 'document' : 'documente'}
                      </span>
                      {pending > 0 && (
                        <span
                          className="dash-fam-row-meta-item dash-fam-row-meta-item--pending"
                          role="status"
                        >
                          {pending} în procesare
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="dash-fam-row-cta" aria-hidden>
                    Deschide <IconArrow />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default FamiliesPanel;
